<template>
    <div class="main m-bottom-full">
        <section id="brn-checkout">
            <h2 class="section-title">Reserve your <span class="text-primary">Ride</span></h2>
            <StepIndicator :curr-step="currStep" />
            <div class="container-fluid container-lg">
                <div class="checkout-container">
                    <div class="col-xl-auto col-lg-8 col-12 checkout-info">
                        <transition name="fade">
                            <div v-show="currStep === 1">
                                <CheckoutOrderInformation :pages="pages"/>
                            </div>
                        </transition>
                        <transition name="fade">
                            <CheckoutContactInformation
                                v-if="currStep === 2"
                                :curr-step="currStep"
                                :referrer="referrer"
                                :with-address2=false
                                ref="contactInformation"
                                :stripe-submit="stripeSubmit"
                                :disable-loader="stripeDisableLoader"
                            />
                        </transition>
                    </div>
                    <CheckoutSummary class="col-xl-auto col-lg-4 col-12">
                        <template v-if="!isEmpty">
                            <div v-if="!usedPromoCode" class="input-group mb-3">
                                <input class="form-control" type="text" placeholder="Discount code" v-model="promoCode">
                                <button v-if="promoCode.length > 0" class="btn btn-outline-secondary" type="button" id="button-addon2" @click.prevent="applyPromoCode(promoCode)">Use</button>
                            </div>
                            <button type="button" class="btn btn-primary next-step" :disabled="loadingState" :class="{ disabled: loadingState }" @click="nextStep">
                                {{ loadingState ? 'Loading...' : payButtonTitle }}
                            </button>
<!--                            <div v-show="currStep === 2" id="iframesSubmit" class="btn btn-primary next-step" :class="{ disabled: loadingState }">-->
<!--                                {{ loadingState ? 'Loading...!!!' : 'Next step !!!' }}-->
<!--                            </div>-->
<!--                            <div v-if="currStep === 2">-->
<!--                                <Paypal :referrer="referrer" :ref-contact-information="$refs.contactInformation" />-->
<!--                                <GooglePay v-if="amount > 0" :referrer="referrer" :ref-contact-information="$refs.contactInformation" :amount="amount"  />-->
<!--                                <ApplePay v-if="amount > 0" :referrer="referrer" :ref-contact-information="$refs.contactInformation" :amount="amount" />-->
<!--                            </div>-->
                        </template>
                    </CheckoutSummary>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import NumberSelector from "../components/elements/NumberSelector";
import StepIndicator from "../components/StepIndicator";
import PaymentCreditCard from "../components/forms/PaymentCreditCard";
import BorderDecoration from "../components/elements/BorderDecoration";
import CheckoutSummary from "../components/blocks/checkout/CheckoutSummary";
import CheckoutOrderInformation from "../components/blocks/checkout/CheckoutOrderInformation";
import CheckoutContactInformation from "../components/blocks/checkout/CheckoutContactInformation";
import CheckoutAccessories from "../components/blocks/checkout/CheckoutAccessories";
import {mapActions, mapGetters} from "vuex";
import ApiRequest from "../../utils/api-request";
import {DateTime} from "luxon";
import LoaderMixin from "../../mixins/LoaderMixin";
import CheckoutResult from "../components/blocks/checkout/CheckoutResult";
import FareHarborMixin from "../../mixins/FareHarborMixin";
import PageLayoutContentMixin from "../../mixins/ContentMixin";
import CartMixin from "../../mixins/CartMixin";
import Paypal from "../components/blocks/checkout/Paypal.vue";
import GooglePay from "../components/blocks/checkout/GooglePay.vue";
import ApplePay from "../components/blocks/checkout/ApplePay.vue";

export default {
    name: "Checkout",
    components: {
        ApplePay,
        GooglePay,
        Paypal,
        CheckoutResult,
        CheckoutAccessories,
        CheckoutContactInformation,
        CheckoutOrderInformation,
        CheckoutSummary, BorderDecoration, PaymentCreditCard, StepIndicator, NumberSelector},
    mixins: [LoaderMixin, FareHarborMixin, PageLayoutContentMixin, CartMixin],
    data: function() {
        return {
            currStep: 1,
            promoCode: '',
            checkoutResult: null,
            amount: 0,
            stripeSubmit: false
        }
    },
    computed: {
        ...mapGetters('app', ['firstName', 'lastName', 'email', 'phone', 'address2', 'settings', 'currentEventId', 'stripeKey']),
        ...mapGetters('filter', ['journeyDate']),
        ...mapGetters('cart', ['isEmpty', 'totalPrice', 'totalTax', 'totalDiscount']),
        ...mapGetters('cart', {
            usedPromoCode: 'promoCode',
            cartBikes: 'bikes',
            cartEvents: 'events',
            cartTours: 'tours',
            cartAccessories: 'accessories',
            cartMerchandises: 'merchandises',
            cartAdditionalOptions: 'additionalOptions'
        }),
        ...mapGetters('payment', ['creditCard']),
        referrer() {
            return document.querySelector('meta[name="ref"]')?.content ?? null;
        },
        payButtonTitle() {
            return this.currStep === 1 ? 'Next step' : 'Purchase'
        }
    },
    mounted() {
        this.cancelStripePayment();
    },
    methods: {
        ...mapActions('cart', ['usePromoCode']),
        ...mapActions('payment', ['initCreditCardPayment']),

        stripeDisableLoader() {
            this.stripeSubmit = false;
            this.disableLoader();
        },

        cancelStripePayment() {
            let data = sessionStorage.getItem("_last_payment_id");

            if(!data) {
                return;
            }

            sessionStorage.removeItem("_last_payment_id");

            const payload = {
                paymentIntent: data,
                stripeKey: this.stripeKey
            }
            new Promise((resolve, reject) => {
                new ApiRequest().cancelStripePaymentIntent(payload).then((response) => {
                    resolve();
                }).catch((error) => {
                    reject(error);
                });
            });
        },

        checkoutAmount() {
            const payload = this.createPayload('', {referrer: this.referrer, isProduct: false})
            new ApiRequest().checkoutAmount(payload).then((response) => {
                if(response.data.status) {
                    this.amount = response.data.card.total ? parseInt(response.data.card.total)/100 : 0;
                }
            }).catch((error) => {
                console.error(error);
            });
        },

        applyPromoCode(code) {
            this.usePromoCode(code).then(response => {
                if (!response) {
                    this.$notify({
                        title: 'Oops!',
                        text: 'Can\'t use promo code, while there are discounted items in cart!'
                    });
                    return;
                }

                if (response.data.status) {
                    this.$notify({
                        title: 'Accepted!',
                        text: 'Promo-code was successfully accepted!'
                    });
                } else {
                    this.$notify({
                        title: 'Wrong promo-code!',
                        text: response.data.message
                    });
                }

            });

        },

        sendPaymentData() {
            let payload = {
                payloadType: 'pay',
                recaptchaToken: token,
                debug: process.env.NODE_ENV === 'development',
                ref: this.referrer,
                account: this.creditCard.account.replace(/\s+/g, ''),
                expirationDate: this.creditCard.expirationYear + this.creditCard.expirationMonth,
                cvv2: this.creditCard.cvv,
                firstName: this.firstName,
                lastName: this.lastName,
                email: this.email,
                phone: this.phone,
                address: this.address2.firstLine,
                postalCode: this.address2.zipCode,
                discount_code: this.usedPromoCode ? this.usedPromoCode.code : null,
            }

            // this part of JSON data object is common
            let data = {
                accessories: this.cartAccessories.map(i => ({
                    accessory_id: i.item.accessory_id,
                    price_id: i.priceOption.price_id,
                    qty: i.amount
                })),
                merchandises: this.cartMerchandises.map(i => ({
                    merchandise_id: i.item.id,
                    size: i.priceOption.size,
                    qty: i.amount
                })),
                additional_options: this.cartAdditionalOptions.map(i => ({
                    additional_option_id: i.item.id,
                    qty: i.amount
                }))
            }

            // structure for rentals checkout
            if (this.cartBikes.length) {
                payload = { ...payload,
                    start: DateTime.fromISO(this.journeyDate).toFormat('yyyy-MM-dd') + " 00:00:00",
                    type: 'rental',
                    data: JSON.stringify({
                        ...data,
                        bikes: this.cartBikes.map(i => ({
                            type_id: i.item.type_id,
                            price_id: i.priceOption.price_id,
                            qty: i.amount,
                            insurance_qty: i.insuranceAmount
                        }))
                    })
                }
            }

            // structure for rentals checkout
            if (this.cartEvents.length) {
                payload = { ...payload,
                    start: DateTime.fromISO(this.journeyDate).toFormat('yyyy-MM-dd') + " 00:00:00",
                    type: 'event',
                    data: JSON.stringify({
                        ...data,
                        event_id: this.cartEvents[0].item.event_id,
                        bikes: this.cartEvents.map(i => ({
                            type_id: i.item.type_id,
                            price_id: i.priceOption.price_id,
                            qty: i.amount,
                            insurance_qty: i.insuranceAmount
                        }))
                    })
                }
            }

            // structure for tour checkout
            if (this.cartTours.length) {
                payload = { ...payload,
                    start: DateTime.fromISO(this.cartTours[0].slot.date).toFormat('yyyy-MM-dd') + ' ' + this.cartTours[0].slot.time,
                    type: 'tour',
                    data: JSON.stringify({
                        ...data,
                        tour_id: this.cartTours[0].item.id,
                        bikes: this.cartTours[0].item.bike_types.filter(j => (j.amount > 0)).map(j => ({
                            type_id: j.type_id,
                            price_id: null,
                            qty: j.amount,
                            insurance_qty: j.insuranceAmount
                        }))
                    })
                }
            }

            new ApiRequest().checkout(payload).then((response) => {

                if (!response.data.status) {
                    this.$notify({
                        text: response.data.message,
                        type: 'error'
                    });
                    return;
                }

                this.initCreditCardPayment();

                try {
                    gtag('event', 'conversion', {
                        'send_to': window.gtag_send_to,
                        'value': ((this.totalPrice + this.totalTax - this.totalDiscount) / 100),
                        'currency': 'USD',
                        'transaction_id': response.data.info.qr,
                        'ref': this.referrer
                    });
                } catch (e) {
                    console.error(e.message);
                }

                location.href = '/checkout-success/thank-you-' + payload.type + '/' + btoa(JSON.stringify({ data: response.data }));

            }).catch((error) => {

                this.$notify({
                    type: 'error',
                    title: 'Server error!',
                    text: 'Something went wrong. Please contact our support service',
                    duration: 5000
                });
            }).finally(() => {
                this.disableLoader();
            });
        },

        isSafari() {
            try {
                return navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1
            } catch (e) {
                console.error(e.message);
                return false;
            }
        },

        async nextStep() {
            this.enableLoader();
            switch (this.currStep) {
                case 1:
                    this.currStep++;
                    this.checkoutAmount();
                    this.disableLoader();
                    break;
                case 2:

                    if (!this.$refs.contactInformation.validate()) {
                        this.disableLoader();
                        return;
                    }

                    let token = '';

                    if (this.$recaptchaSiteKey && !this.isSafari()) {
                        await this.$recaptchaLoaded()
                        token = await this.$recaptcha('submit')
                    }

                    this.stripeSubmit = true;
                    break;
            }
        },
    },
    beforeMount() {
        if (this.fareHarborMode()) {
            window.location.href = '/';
        }
    }
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
}
</style>
